import { useState, useCallback } from "react";
import apiClient from "../api/client";
import { config } from "../config";

export const useSecrets = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSecrets = useCallback(async (platformName, headers) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/${platformName}/secrets`, {
        headers,
      });
      const secretsWithIds = response.data.map((secret, index) => ({
        ...secret,
        id: index + 1,
      }));

      // Normalize tag format
      const normalizedSecrets = secretsWithIds.map((secret) => {
        if (platformName !== config.platforms[0]) {
          const formattedTags = Object.entries(secret.tags || {}).map(
            ([key, value]) => ({ Key: key, Value: value })
          );
          return { ...secret, tags: formattedTags };
        }
        return secret;
      });

      return normalizedSecrets;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSecret = useCallback(
    async (secretName, platformName, headers, version = "current") => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get(
          `/${platformName}/secrets/${secretName}`,
          {
            headers: { ...headers, Version: version },
          }
        );
        return response.data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createSecret = useCallback(
    async (secretData, platformName, headers) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.post(
          `/${platformName}/secrets`,
          secretData,
          { headers }
        );
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateSecret = useCallback(
    async (secretName, secretData, platformName, headers) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.put(
          `/${platformName}/secrets/${secretName}`,
          secretData,
          { headers }
        );
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteSecret = useCallback(
    async (secretName, platformName, headers) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.delete(
          `/${platformName}/secrets/${secretName}`,
          { headers }
        );
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const rollbackSecret = useCallback(
    async (secretName, platformName, headers) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.post(
          `/${platformName}/secrets/${secretName}`,
          {},
          { headers }
        );
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    loading,
    error,
    fetchSecrets,
    fetchSecret,
    createSecret,
    updateSecret,
    deleteSecret,
    rollbackSecret,
  };
};
